/**
 * Message Constants
 * Centralized error, success, and informational messages
 */
// Authentication Messages
export const AUTH_MESSAGES = {
    // Errors
    NO_TOKEN: 'No token provided',
    INVALID_TOKEN: 'Invalid token',
    INVALID_SESSION: 'Invalid session',
    SESSION_EXPIRED: 'Session expired',
    INVALID_CREDENTIALS: 'Invalid credentials',
    ACCOUNT_DISABLED: 'Account is disabled',
    USER_NOT_FOUND: 'User not found or inactive',
    NOT_AUTHENTICATED: 'Not authenticated',
    AUTHENTICATION_REQUIRED: 'Authentication required',
    AUTHENTICATION_FAILED: 'Authentication failed',
    // Success
    LOGIN_SUCCESS: 'Logged in successfully',
    LOGOUT_SUCCESS: 'Logged out successfully',
    SESSION_CREATED: 'Session created successfully',
};
// Authorization Messages
export const AUTHZ_MESSAGES = {
    INSUFFICIENT_PERMISSIONS: 'Insufficient permissions',
    FORBIDDEN: 'Forbidden: Insufficient permissions',
    UNAUTHORIZED: 'Unauthorized',
};
// Validation Messages
export const VALIDATION_MESSAGES = {
    VALIDATION_FAILED: 'Validation failed',
    INVALID_EMAIL: 'Invalid email format',
    INVALID_USERNAME: 'Username must be 3-20 characters and contain only letters, numbers, hyphens, and underscores',
    PASSWORD_REQUIRED: 'Password is required',
    PASSWORD_TOO_SHORT: 'Password must be at least 8 characters',
    PASSWORD_REQUIREMENTS: 'Password must contain at least uppercase, lowercase, number, and special character',
    NAME_REQUIRED: 'Name is required',
    EMAIL_REQUIRED: 'Email is required',
};
// Database Messages
export const DB_MESSAGES = {
    CONNECTION_ERROR: 'Database connection error',
    QUERY_ERROR: 'Database query error',
    NOT_FOUND: 'Resource not found',
    ALREADY_EXISTS: 'Resource already exists',
    DELETE_FAILED: 'Failed to delete resource',
    UPDATE_FAILED: 'Failed to update resource',
    CREATE_FAILED: 'Failed to create resource',
    UNKNOWN_ERROR: 'Unknown database error',
};
// Generic Error Messages
export const ERROR_MESSAGES = {
    INTERNAL_SERVER_ERROR: 'Internal server error',
    NOT_FOUND: 'Not found',
    BAD_REQUEST: 'Bad request',
    SOMETHING_WENT_WRONG: 'Something went wrong',
    UNKNOWN_ERROR: 'Unknown error',
    SESSION_NOT_FOUND: 'Session not found',
};
// Client Messages
export const CLIENT_MESSAGES = {
    DELETE_WITH_PROJECTS: 'Cannot delete client with existing projects',
    CLIENT_NOT_FOUND: 'Client not found',
    CLIENT_CREATED: 'Client created successfully',
    CLIENT_UPDATED: 'Client updated successfully',
    CLIENT_DELETED: 'Client deleted successfully',
};
// User Messages
export const USER_MESSAGES = {
    USER_NOT_FOUND: 'User not found',
    USER_CREATED: 'User created successfully',
    USER_UPDATED: 'User updated successfully',
    USER_DELETED: 'User deleted successfully',
    SAVE_ERROR: 'An error occurred while saving the user',
};
// Session Messages
export const SESSION_MESSAGES = {
    UPDATE_FAILED: 'Failed to update session lastAccessedAt',
};
// Case Messages
export const CASE_MESSAGES = {
    CASE_NOT_FOUND: 'Case not found',
    CASE_CREATED: 'Case created successfully',
    CASE_UPDATED: 'Case updated successfully',
    CASE_DELETED: 'Case deleted successfully',
    INVALID_SLUG: 'Invalid slug format',
    INVALID_ID: 'Invalid case ID format',
};
// Category Messages
export const CATEGORY_MESSAGES = {
    CATEGORY_NOT_FOUND: 'Category not found',
    CATEGORY_CREATED: 'Category created successfully',
    CATEGORY_UPDATED: 'Category updated successfully',
    CATEGORY_DELETED: 'Category deleted successfully',
    INVALID_ID: 'Invalid ID format',
    DELETE_WITH_PROJECTS: 'Cannot delete category with existing projects',
    FAILED_TO_FETCH: 'Failed to fetch categories',
    FAILED_TO_CREATE: 'Failed to create category',
    FAILED_TO_UPDATE: 'Failed to update category',
    FAILED_TO_DELETE: 'Failed to delete category',
    FAILED_TO_REORDER: 'Failed to reorder categories',
    REORDERED_SUCCESS: 'Categories reordered successfully',
    AREA_NOT_FOUND: 'Area not found',
    AREA_CREATED: 'Area created successfully',
    AREA_UPDATED: 'Area updated successfully',
    AREA_DELETED: 'Area deleted successfully',
    AREA_SLUG_EXISTS: 'An area with this slug already exists. Please use a different name.',
    FAILED_TO_CREATE_AREA: 'Failed to create area',
    FAILED_TO_UPDATE_AREA: 'Failed to update area',
    FAILED_TO_DELETE_AREA: 'Failed to delete area',
    FAILED_TO_FETCH_AREAS: 'Failed to fetch areas',
    FAILED_TO_REORDER_AREAS: 'Failed to reorder areas',
    AREAS_REORDERED_SUCCESS: 'Areas reordered successfully',
    SERVICE_NOT_FOUND: 'Service not found',
    SERVICE_CREATED: 'Service created successfully',
    SERVICE_UPDATED: 'Service updated successfully',
    SERVICE_DELETED: 'Service deleted successfully',
    FAILED_TO_CREATE_SERVICE: 'Failed to create service',
    FAILED_TO_UPDATE_SERVICE: 'Failed to update service',
    FAILED_TO_DELETE_SERVICE: 'Failed to delete service',
    FAILED_TO_REORDER_SERVICES: 'Failed to reorder services',
    SERVICES_REORDERED_SUCCESS: 'Services reordered successfully',
};
// Analytics Messages
export const ANALYTICS_MESSAGES = {
    FAILED_TO_FETCH_KPIS: 'Failed to fetch dashboard KPIs',
    FAILED_TO_FETCH_ACTIVITY: 'Failed to fetch recent activity',
    FAILED_TO_FETCH_BY_PERIOD: 'Failed to fetch analytics by period',
    INVALID_PAGINATION: 'Invalid pagination parameters',
    PAGINATION_DETAILS: 'Page must be >= 1, limit must be between 1 and 100',
    INVALID_PERIOD: 'Invalid period parameter',
    PERIOD_DETAILS: 'Period must be one of: 7D, 30D, 90D, 1Y',
};
// Bug Report Messages
export const BUG_REPORT_MESSAGES = {
    DESCRIPTION_REQUIRED: 'Bug description is required',
    DESCRIPTION_TOO_LONG: 'Bug description is too long (max 5000 characters)',
    INVALID_URL: 'Invalid URL format',
    REPORT_SENT: 'Bug report sent successfully',
    FAILED_TO_SEND: 'Failed to send bug report',
    UNAUTHORIZED: 'Unauthorized',
};
// Metrics Messages
export const METRICS_MESSAGES = {
    METRIC_NOT_FOUND: 'Metric not found',
    METRIC_ID_REQUIRED: 'Metric ID is required',
    METRIC_CREATED: 'Metric created successfully',
    METRIC_UPDATED: 'Metric updated successfully',
    METRIC_DELETED: 'Metric deleted successfully',
};
// Upload Messages
export const UPLOAD_MESSAGES = {
    NO_FILE: 'No file provided',
    NO_FILES: 'No files provided',
    UPLOAD_FAILED: 'Failed to upload image',
    MISSING_PATH: 'File path is required',
    DELETE_FAILED: 'Failed to delete file',
    INTERNAL_ERROR: 'Internal server error',
};
// Error Codes (machine-readable codes for API responses)
export const ERROR_CODES = {
    // Upload errors
    NO_FILE: 'NO_FILE',
    NO_FILES: 'NO_FILES',
    UPLOAD_FAILED: 'UPLOAD_FAILED',
    MISSING_PATH: 'MISSING_PATH',
    DELETE_FAILED: 'DELETE_FAILED',
    // Generic errors
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    NOT_FOUND: 'NOT_FOUND',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
};
// Invitation Messages
export const INVITATION_MESSAGES = {
    NOT_AUTHENTICATED: 'Not authenticated',
    VALIDATION_FAILED: 'Validation failed',
    SENT_SUCCESS: 'Invitation sent successfully',
    INVALID_QUERY: 'Invalid query parameters',
    TOKEN_REQUIRED: 'Token is required',
    ACCOUNT_CREATED: 'Account created successfully. You can now login.',
    REVOKED_SUCCESS: 'Invitation revoked successfully',
    INVITATION_NOT_FOUND: 'Invitation not found',
    RESENT_SUCCESS: 'Invitation resent successfully',
    INTERNAL_ERROR: 'Internal server error',
};
// Tag Messages
export const TAG_MESSAGES = {
    TAG_NOT_FOUND: 'Tag not found',
    TAG_ALREADY_EXISTS: 'A tag with this name already exists',
    TAG_CREATED: 'Tag created successfully',
    TAG_UPDATED: 'Tag updated successfully',
    TAG_DELETED: 'Tag deleted successfully',
    TAG_IN_USE: 'Cannot delete tag that is used by',
    TAGS_MERGED: 'Tags merged successfully',
    TAGS_ARRAY_REQUIRED: 'Tags array is required',
    VALIDATION_FAILED: 'Validation failed',
    INTERNAL_ERROR: 'Internal server error',
    NOT_AUTHENTICATED: 'Not authenticated',
    ONE_OR_MORE_NOT_FOUND: 'One or more tags not found',
};
// Taxonomy Messages
export const TAXONOMY_MESSAGES = {
    TAXONOMY_NOT_FOUND: 'Taxonomy not found',
    INVALID_ID: 'Invalid ID format',
    INVALID_QUERY: 'Invalid query parameters',
    INVALID_REQUEST: 'Invalid request body',
    INTERNAL_ERROR: 'Internal server error',
    CANNOT_DELETE_WITH_TERMS: 'Cannot delete taxonomy with active terms. Delete terms first or use soft delete.',
};
// Term Messages
export const TERM_MESSAGES = {
    TERM_NOT_FOUND: 'Term not found',
    INVALID_ID: 'Invalid ID format',
    INVALID_QUERY: 'Invalid query parameters',
    INVALID_REQUEST: 'Invalid request body',
    INTERNAL_ERROR: 'Internal server error',
};
// Translation Messages
export const TRANSLATION_MESSAGES = {
    TRANSLATION_NOT_FOUND: 'Translation not found',
    TRANSLATIONS_ARRAY_REQUIRED: 'Translations must be an array',
    UNAUTHORIZED: 'Unauthorized',
    CREATED_SUCCESS: 'Translation created successfully',
    UPDATED_SUCCESS: 'Translation updated successfully',
    DELETED_SUCCESS: 'Translation deleted successfully',
    BULK_OPERATION_SUCCESS: 'Bulk operation completed',
    INTERNAL_ERROR: 'Internal server error',
};
// Alert Banner Messages
export const ALERT_BANNER_MESSAGES = {
    NOT_FOUND: 'Alert banner not found',
    INVALID_ID: 'Invalid ID format',
    INVALID_QUERY: 'Invalid query parameters',
    INVALID_REQUEST: 'Invalid request body',
    CREATED: 'Alert banner created successfully',
    UPDATED: 'Alert banner updated successfully',
    DELETED: 'Alert banner deleted successfully',
    ACTIVATED: 'Alert banner activated',
    DEACTIVATED: 'Alert banner deactivated',
    INTERNAL_ERROR: 'Internal server error',
};
